import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { useState } from 'react'

interface TableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  isLoading?: boolean
  enableSorting?: boolean
}

export function Table<TData>({ 
  data, 
  columns, 
  isLoading, 
  enableSorting = true 
}: TableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
  })

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="animate-pulse">
          <div className="h-10 bg-muted rounded mb-4"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded mb-2"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full overflow-x-auto">
      <table className="w-full h-full  border-collapse table-fixed">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="h-[52px] border-b-[2px] border-b-[#7C797C]"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`px-3 py-3 font-semibold text-[16px] text-left text-foreground ${
                    header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-muted' : ''
                  }`}
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center gap-2">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {enableSorting && header.column.getCanSort() && (
                      <span className="text-muted-foreground">
                        {header.column.getIsSorted() === 'desc' ? ' ↓' : 
                         header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="h-[80px] border-b border-[#B8B5B6] hover:bg-muted">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-4 align-middle text-left overflow-hidden">
                    <div className="truncate">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-8 text-center text-muted-foreground"
              >
                No results found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}